import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import qs from 'qs';
import { t } from 'ttag';

import { RiHeartFill, RiHeartLine } from 'react-icons/ri';
import { HiLockClosed } from 'react-icons/hi';

import client from '../../js/utils/client';

export default function ProductCard({
  id,
  url,
  image,
  imageAlt,
  labels,
  title,
  min_price,
  max_price,
  rrp,
  discountPercentage,
  currency,
  sizes,
  isAuthenticated,
  isExclusive,
  isFavourite,
  setShowLoginModal,
  disabled
}) {
  const [filteredLabels, setFilteredLabels] = useState(
    /** @type {{}[]} An Array of Filtered Labels to be displayed on the Product Card */ []
  );
  const [isFavourited, setIsFavourited] = useState(
    /** @type {boolean} Determines whether the Product is Favourited by the User */ isFavourite
  );

  /**
   * @type {string} - The Locale of the Site in ISO 639-1 format.
   */
  const siteLocale = useMemo(
    () => window?.config?.iso_language_code || 'en-GB',
    []
  );

  /**
   * Takes a Price, Currency and Locale and returns a Formatted Price with the Currency Symbol.
   * @param {number} price - The Price of the Product.
   * @param {string} priceCurrency - The Currency of the Site in ISO 4217 Format.
   * @param {string} locale - The Locale of the Site in da-DK Format.
   * @see https://en.wikipedia.org/wiki/ISO_4217
   * @returns {string} - The Price formatted in the correct Locale.
   * @type {function(number, string, string): string}
   */
  const priceFormatter = useCallback((price, priceCurrency, locale) => {
    if (price % 1 === 0) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: priceCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);
    }
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: priceCurrency,
      minimumFractionDigits: 2
    }).format(price);
  }, []);

  /**
   * Favours a Product if it is not already favourited. Unfavourites a Product if it is already favourited.
   * Requires the User to be Authenticated. If the User is not Authenticated, the Login Modal is shown.
   * @param {number|string} productId - The Product ID
   * @type {(function(number|string): void)}
   */
  const handleFavouriteClick = useCallback(
    productID => {
      if (isAuthenticated) {
        const postUrl = isFavourited
          ? '/api/favorites/remove/'
          : '/api/favorites/add/';

        client
          .post(postUrl, qs.stringify({ product_id: productID }))
          .then(() => {});

        setIsFavourited(!isFavourited);
      } else {
        setShowLoginModal(true);
      }
    },
    [isAuthenticated, isFavourited]
  );

  /**
   * Filters and Sorts the Labels to be displayed on the Product Card.
   * @type {React.useEffect<void>}
   */
  useEffect(() => {
    setFilteredLabels(
      labels
        .filter(label => !label.discount && label.active)
        .sort((a, b) => a.priority - b.priority)
    );
  }, [labels]);

  return (
    <li className="max-w-[225px]">
      <div className="relative">
        {image && (
          <a
            href={url}
            className={clsx(
              'flex flex-col gap-1 no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2',
              {
                'pointer-events-none cursor-not-allowed opacity-50': disabled
              }
            )}
            tabIndex={disabled ? -1 : 0}
          >
            <div className="aspect-h-1 aspect-w-1 w-full">
              <img
                className="w-full overflow-hidden object-cover"
                data-testid="product-image"
                src={image}
                alt={imageAlt || ''}
              />
            </div>
          </a>
        )}
        <button
          type="button"
          onClick={() => handleFavouriteClick(id)}
          aria-label={t`Add ${title} to Favourites`}
          className={clsx(
            'absolute top-0 right-0 m-0 flex items-center border-0 bg-transparent p-2 hover:cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:p-4',
            {
              'text-black': isFavourited,
              'text-dark-gray hover:text-black': !isFavourited
            }
          )}
          disabled={disabled}
        >
          {isFavourited ? (
            <RiHeartFill className="h-6 w-6" />
          ) : (
            <RiHeartLine className="h-6 w-6" />
          )}
        </button>
        <ul className="absolute bottom-2 left-2 m-0 flex list-none flex-col items-start gap-1 p-0">
          {filteredLabels &&
            filteredLabels.map(label => (
              <li
                className="hidden px-2 py-1 text-xs font-semibold first:block md:block"
                style={{
                  backgroundColor: filteredLabels.background_color || '#000000',
                  color: filteredLabels.color || '#FFFFFF'
                }}
              >
                {label.name}
              </li>
            ))}
        </ul>
      </div>

      <a
        href={url}
        className={clsx(
          'flex flex-col gap-1 p-1 no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2',
          {
            'pointer-events-none cursor-not-allowed opacity-50': disabled
          }
        )}
        tabIndex={disabled ? -1 : 0}
      >
        <div className="min-h-[32px]">
          <div
            data-bv-show="inline_rating"
            data-bv-product-id={id}
            data-bv-seo="false"
          />
        </div>

        {title && (
          <p className="m-0 text-sm font-semibold text-black line-clamp-3 md:text-base">
            {isExclusive && (
              <span className="mr-1 inline-flex items-center text-green">
                <HiLockClosed className="h-3 w-3" />
              </span>
            )}
            {title}
          </p>
        )}

        {min_price && max_price && (
          <div className="flex flex-col gap-1">
            <span className="text-base text-black md:text-lg">
              {min_price < max_price && (
                <span className="mr-1 text-xs font-normal text-dark-gray md:text-sm">
                  {t`From`}
                </span>
              )}
              {priceFormatter(min_price, currency, siteLocale)}
            </span>
            {rrp && discountPercentage > 0 && (
              <div className="flex min-h-[28px] flex-wrap items-center gap-2">
                <span className="text-sm font-normal text-dark-gray line-through md:text-base">
                  {priceFormatter(rrp, currency, siteLocale)}
                </span>
                {discountPercentage >= 20 && (
                  <span className="flex bg-green px-2 py-1 text-sm font-bold text-white">
                    {`-${discountPercentage}%`}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {Array.isArray(sizes) && sizes.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {sizes?.length >= 4 ? (
              <span className="text-sm text-dark-gray">
                {t`Many sizes available`}
              </span>
            ) : (
              <>
                <span className="text-sm text-black">{t`Sizes`}: </span>
                {sizes.map((size, index) => {
                  return (
                    <span
                      key={size.name_short}
                      className="text-sm text-dark-gray"
                    >
                      {size.name_short}
                      {index !== sizes.length - 1 ? ', ' : ''}
                    </span>
                  );
                })}
              </>
            )}
          </div>
        )}
      </a>
    </li>
  );
}

ProductCard.propTypes = {
  /**
   * Product ID
   */
  id: PropTypes.string.isRequired,
  /**
   * Product URL
   */
  url: PropTypes.string,
  /**
   * The Product image to display inside the Card.
   */
  image: PropTypes.string,
  /**
   * The Alternative Text for the Product image.
   */
  imageAlt: PropTypes.string,
  /**
   * The Labels to display inside the Card.
   */
  labels: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The Label Text.
       */
      name: PropTypes.string.isRequired,
      /**
       * Determines if the Label is a Discount Label.
       */
      discount: PropTypes.bool.isRequired,
      /**
       * The Priority of the Label.
       */
      priority: PropTypes.number.isRequired
    })
  ),
  /**
   * The Product Title to display inside the Card.
   */
  title: PropTypes.string,
  /**
   * The Minimum Price of the Product.
   */
  min_price: PropTypes.number,
  /**
   * The Maximum Price of the Product.
   */
  max_price: PropTypes.number,
  /**
   * The Product RRP to display inside the Card.
   */
  rrp: PropTypes.number,
  /**
   * The Product Discount Percentage to display inside the Card.
   */
  discountPercentage: PropTypes.number,
  /**
   * The Price Currency to display inside the Card.
   */
  currency: PropTypes.string,
  /**
   * The available Sizes to display inside the Card.
   */
  sizes: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The Size Name.
       */
      name: PropTypes.string.isRequired
    })
  ),
  /**
   * Determines if the User is authenticated.
   */
  isAuthenticated: PropTypes.bool,
  /**
   * Determines if the Product is Exclusive.
   */
  isExclusive: PropTypes.bool,
  /**
   * Determines if the Product is a Favourite.
   */
  isFavourite: PropTypes.bool,
  /**
   * The React.useState Hook to set the showLoginModal state, if the User attempts to favorite
   * a Product without being authenticated.
   */
  setShowLoginModal: PropTypes.func,
  /**
   * Determines if the Product Card is disabled.
   */
  disabled: PropTypes.bool
};

ProductCard.defaultProps = {
  url: '',
  image: '',
  imageAlt: '',
  labels: [],
  title: '',
  min_price: undefined,
  max_price: undefined,
  rrp: undefined,
  discountPercentage: 0,
  currency: '',
  sizes: [],
  isFavourite: false,
  isExclusive: false,
  isAuthenticated: false,
  setShowLoginModal: () => {},
  disabled: false
};
